import { useCallback, useEffect, useMemo, useState } from "react";
import { Auth } from "shared/@auth/Auth";
import createNotification from "shared/@utils/createNotification";

const emptySelectedFilters = {
  year: new Date().getFullYear().toString(),
  month: "",
  monthString: "",
  eventType: "",
  report: "",
  groupID: "",
};

export function useEventQualifications(iboNumber) {
  const { makeAuthedRequest } = Auth.useContainer();

  const [filters, setFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState(emptySelectedFilters);

  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchFilters = useCallback(async () => {
    if (!iboNumber) return;

    setFiltersLoading(true);

    try {
      const res = await makeAuthedRequest(
        `/v1/membership/pulse/qualifications/filters/ibo/${iboNumber}`
      );

      if (!res.ok) {
        throw new Error("Failed to load filters.");
      }

      const data = await res.json();
      const nextFilters = Array.isArray(data) ? data : [];

      setFilters(nextFilters);
      setSelectedFilters(emptySelectedFilters);
      setReportData(null);
    } catch (error) {
      setFilters([]);
      setSelectedFilters(emptySelectedFilters);
      setReportData(null);

      createNotification({
        type: "danger",
        title: "Qualifications",
        message: error.message,
        autoDismiss: true,
      });
    } finally {
      setFiltersLoading(false);
    }
  }, [iboNumber, makeAuthedRequest]);

  const selectedFilterRow = useMemo(() => {
    return (
      filters.find(
        (item) =>
          String(item.year) === String(selectedFilters.year) &&
          String(item.monthString) === String(selectedFilters.monthString) &&
          String(item.eventType) === String(selectedFilters.eventType) &&
          String(item.report) === String(selectedFilters.report)
      ) || null
    );
  }, [filters, selectedFilters]);

  useEffect(() => {
    if (!selectedFilterRow) return;

    setSelectedFilters((current) => ({
      ...current,
      month: selectedFilterRow.month || "",
      groupID: selectedFilterRow.groupID || "",
    }));
  }, [selectedFilterRow]);

  const fetchReport = useCallback(
    async (activeFilters, options = { isRefresh: false }) => {
      if (
        !iboNumber ||
        !activeFilters.groupID ||
        !activeFilters.year ||
        !activeFilters.month
      ) {
        setReportData(null);
        return;
      }

      if (options.isRefresh) {
        setIsRefreshing(true);
      } else {
        setReportLoading(true);
      }

      try {
        const res = await makeAuthedRequest(
          `/v1/membership/pulse/qualifications/leg-report/ibo/${iboNumber}/groupId/${activeFilters.groupID}/year/${activeFilters.year}/month/${activeFilters.month}`
        );

        if (!res.ok) {
          throw new Error("Failed to load qualification report.");
        }

        const data = await res.json();
        setReportData(data || null);
      } catch (error) {
        createNotification({
          type: "danger",
          title: "Qualifications",
          message: error.message,
          autoDismiss: true,
        });
      } finally {
        if (options.isRefresh) {
          setIsRefreshing(false);
        } else {
          setReportLoading(false);
        }
      }
    },
    [iboNumber, makeAuthedRequest]
  );

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (
      selectedFilters.year &&
      selectedFilters.month &&
      selectedFilters.eventType &&
      selectedFilters.report &&
      selectedFilters.groupID
    ) {
      fetchReport(selectedFilters, { isRefresh: false });
    } else {
      setReportData(null);
    }
  }, [selectedFilters, fetchReport]);

  const onChangeFilter = useCallback(
    (key, value) => {
      setSelectedFilters((current) => {
        const next = {
          ...current,
          [key]: value,
        };

        if (key === "year") {
          return {
            year: value,
            month: "",
            monthString: "",
            eventType: "",
            report: "",
            groupID: "",
          };
        }

        if (key === "monthString") {
          const eventStillExists = filters.some(
            (item) =>
              String(item.year) === String(current.year) &&
              String(item.monthString) === String(value) &&
              String(item.eventType) === String(current.eventType)
          );

          const reportStillExists = filters.some(
            (item) =>
              String(item.year) === String(current.year) &&
              String(item.monthString) === String(value) &&
              String(item.eventType) === String(current.eventType) &&
              String(item.report) === String(current.report)
          );

          return {
            ...next,
            month: "",
            eventType: eventStillExists ? current.eventType : "",
            report: reportStillExists ? current.report : "",
            groupID: "",
          };
        }

        if (key === "eventType") {
          const reportStillExists = filters.some(
            (item) =>
              String(item.year) === String(current.year) &&
              String(item.monthString) === String(current.monthString) &&
              String(item.eventType) === String(value) &&
              String(item.report) === String(current.report)
          );

          return {
            ...next,
            report: reportStillExists ? current.report : "",
            groupID: "",
          };
        }

        if (key === "report") {
          return {
            ...next,
            groupID: "",
          };
        }

        return next;
      });
    },
    [filters]
  );

  return {
    filters,
    filtersLoading,
    selectedFilters,
    onChangeFilter,
    reportData,
    reportLoading,
    refetchFilters: fetchFilters,
    isRefreshing,
    refetchReport: () => fetchReport(selectedFilters, { isRefresh: true }),
  };
}
